/**
 * useSyncStatus
 *
 * Live counts of sync_queue rows by status. Re-renders when the push engine
 * notifies (after every cycle / mutation). Used by the sidebar badge.
 */

import { useEffect, useState } from "react";

import { db } from "../db/schema";
import { subscribe } from "../services/sync-push.engine";

export interface SyncStatusCounts {
  pending:   number;
  syncing:   number;
  conflict:  number;
  failed:    number;
  /** failed + conflict — the number the badge surfaces. */
  errors:    number;
}

const ZERO: SyncStatusCounts = {
  pending: 0, syncing: 0, conflict: 0, failed: 0, errors: 0,
};

export function useSyncStatus(): SyncStatusCounts {
  const [counts, setCounts] = useState<SyncStatusCounts>(ZERO);

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      const [pending, syncing, conflict, failed] = await Promise.all([
        db.sync_queue.where("status").equals("pending").count(),
        db.sync_queue.where("status").equals("syncing").count(),
        db.sync_queue.where("status").equals("conflict").count(),
        db.sync_queue.where("status").equals("failed").count(),
      ]);
      if (cancelled) return;
      setCounts({ pending, syncing, conflict, failed, errors: conflict + failed });
    };

    refresh();
    const unsub = subscribe(refresh);
    return () => { cancelled = true; unsub(); };
  }, []);

  return counts;
}
